/* eslint-disable @typescript-eslint/naming-convention */
import { OAuthErrorEvent, OAuthService } from 'angular-oauth2-oidc';
import { Inject, Injectable, Optional, PLATFORM_ID } from '@angular/core';
import { combineLatest, from, merge, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  concatMap,
  distinctUntilChanged,
  filter,
  map,
  mapTo,
  mergeMap,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import {
  AuthorizationStateType,
  createAuthConfig,
  FF_ACCOUNT_INFO_V2,
  FF_MAGIC_LINK_AUTH,
  FF_SL_SYNC_SSO_FOR_REACT_APP,
} from '~app/modules/authorization/constants/authorization.constant';
import { AuthorizationState } from '~app/modules/authorization/models/authorization-state.model';
import { Token } from '~core/modules/identity/models/identity.model';
import { IdentityService } from '~core/modules/identity/services/identity.service';
import { environment } from '~env/environment';
import { UrlUtil } from '~shared/utils/url.util';
import { Router, RoutesRecognized } from '@angular/router';
import { ExponeaService } from '~shared/services/exponea.service';
import { isPlatformBrowser } from '@angular/common';
import { ServerLoggerService } from '~shared/modules/server-logger/services/server-logger.service';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { NUMBER_0, NUMBER_1 } from '~shared/constants';
import { AuthorizationApplicationEvent } from '~shared/modules/application-event/models';
import { ApplicationEventService } from '~shared/modules/application-event/services';
import {
  ApplicationAuthorizationActionType,
  LoginEventsLabel,
  LoginType,
  MagicLinkAuthorizationLabel,
} from '~shared/modules/application-event/constants';
import { GATrackingService } from '~shared/modules/ga-tracking/services';
import {
  LOGIN_USER_TYPES,
  METROMARKETS_IMPERSONATE_BUYER,
  USER_TYPE_KEY,
} from '~app/core/modules/identity/constants/identity.constant';
import { AccountType, userTypeByAccountType } from '~shared/models';
import { generateIsSilentAuthDisabled$, isSoftLoggedIn, syncSSOForReactApp } from '~server/utils/auth.utils';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { getCountryDomainCode } from '~server/utils/country-detection.util';

const TIME_OUT_FACTOR_FOR_ACCESS_TOKEN_REFRESH = 0.9;

@Injectable({
  providedIn: 'root',
})

// service should replace IdentityService after FF_COBA_IDAM_AUTH is removed
export class AuthorizationIdamStateService {
  public readonly state$: Observable<AuthorizationState>;
  private readonly IDAM_SSO_REALM = environment.IDAMSSO() && environment.IDAMSSO().realm;
  private url = '';
  private isNormalLoginSubject: Subject<boolean> = new Subject<boolean>();
  private isSilentLoginDisabled$ = generateIsSilentAuthDisabled$(
    this.activeFeaturesConfigCatService,
    this.oauthService
  );
  constructor(
    private oauthService: OAuthService,
    private identityService: IdentityService,
    private router: Router,
    private exponeaService: ExponeaService,
    // eslint-disable-next-line @typescript-eslint/ban-types
    @Inject(PLATFORM_ID) private platformId: Object,
    private serverLogger: ServerLoggerService,
    private activeFeaturesConfigCatService: ActiveFeaturesConfigcatService,
    private readonly applicationEventService: ApplicationEventService,
    private readonly gATrackingService: GATrackingService,
    @Optional() @Inject(REQUEST) private webAppBuyerRequest: any
  ) {
    // eslint-disable-next-line no-console
    console.log('[IdamStateService] create');

    // eslint-disable-next-line no-magic-numbers
    this.state$ = this.createAuthState$().pipe(shareReplay(1));

    this.oauthService.events.subscribe((e) => {
      // eslint-disable-next-line no-console
      console.log(`[IdamStateService] O... event ${e.type}`);
      if (e.type === 'token_expires' && this.identityService.isLoggedIn()) {
        this.oAuthSilentRefresh();
      }
    });

    if (isPlatformBrowser(this.platformId)) {
      this.url = window.location.href;

      this.activeFeaturesConfigCatService
        .isFeatureEnabledReadyForCurrentCountry$(FF_ACCOUNT_INFO_V2)
        .pipe(
          filter(Boolean),
          take(NUMBER_1),
          mergeMap(() =>
            this.identityService.getAuthAccountInfoStatus().pipe(
              filter((status) => Object.values<string>(AccountType).includes(status)),
              take(NUMBER_1),
              map((accountType) => userTypeByAccountType[accountType] ?? 'CUST'),
              mergeMap((userType) => {
                return this.isSilentLoginDisabled$.pipe(map((isDisabled) => [isDisabled, userType]));
              }),
              tap(([isDisabled, userType]) => {
                if (!isDisabled) {
                  this.oauthService.setupAutomaticSilentRefresh(
                    {
                      realm_id: this.IDAM_SSO_REALM,
                      country_code: getCountryDomainCode(this.webAppBuyerRequest).toUpperCase(),
                      user_type: userType,
                    },
                    'access_token',
                    false
                  );
                }
              })
            )
          )
        )
        .subscribe();
    }
  }

  isNormalLogin() {
    return this.isNormalLoginSubject.asObservable();
  }

  async oAuthSilentRefresh() {
    const isDisabled = await this.isSilentLoginDisabled$.toPromise();
    if (!isDisabled) {
      const isEmployeeLoginEnabled = await this.activeFeaturesConfigCatService
        .isFeatureEnabledReadyForCurrentCountry$(FF_ACCOUNT_INFO_V2)
        .pipe(take(NUMBER_1))
        .toPromise();

      const userType = isEmployeeLoginEnabled ? localStorage.getItem(USER_TYPE_KEY) ?? 'CUST' : 'CUST';

      if (userType === LOGIN_USER_TYPES.EMPLOYEE) {
        this.oauthService.scope = `openid role=${METROMARKETS_IMPERSONATE_BUYER}`;
      }

      try {
        await this.oauthService.silentRefresh({
          realm_id: this.IDAM_SSO_REALM,
          country_code: getCountryDomainCode(this.webAppBuyerRequest).toUpperCase(),
          user_type: userType,
        });

        this.applicationEventService.publish(
          new AuthorizationApplicationEvent(
            ApplicationAuthorizationActionType.DestinationLogin,
            LoginEventsLabel.SuccessSilentRefresh
          ),
          true
        );
      } catch {
        this.applicationEventService.publish(
          new AuthorizationApplicationEvent(
            ApplicationAuthorizationActionType.DestinationLogin,
            LoginEventsLabel.FailSilentRefresh
          ),
          true
        );
        this.identityService.unauthorize();
      }
    }
  }

  public initOAuth(): void {
    this.state$.subscribe();
  }

  private createAuthState$() {
    const authState$ = () => {
      this.OAuthErrorEvents();

      const authStateSetup$ = merge(
        of({ type: AuthorizationStateType.Pending }),
        this.listenOAuthLoginEvents$(),
        this.listenOAuthLogoutEvents$(),
        this.configureOAuth$()
      ).pipe(
        distinctUntilChanged((e1, e2) => e1.type === e2.type),
        // eslint-disable-next-line no-magic-numbers
        shareReplay(1)
      );

      return this.activeFeaturesConfigCatService.isFeatureEnabledAndReadyV2(FF_MAGIC_LINK_AUTH).pipe(
        mergeMap((isEnabled) => {
          if (isEnabled) {
            const code = UrlUtil.getQueryParam('code');
            const hasDeviceContext = !!UrlUtil.getQueryParam('device_context');

            if (code && hasDeviceContext) {
              this.gATrackingService.isTrackingStarted$.subscribe({
                complete: () => {
                  this.applicationEventService.publish(
                    new AuthorizationApplicationEvent(
                      ApplicationAuthorizationActionType.MagicLinkEmailRedirect,
                      MagicLinkAuthorizationLabel.Success
                    )
                  );
                },
              });
              const redirectUri = decodeURIComponent(window.location.href.split('?')[NUMBER_0]);

              return this.identityService.authorizeWithCode(code, redirectUri).pipe(mergeMap(() => authStateSetup$));
            }
          }

          return authStateSetup$;
        })
      );
    };

    // eslint-disable-next-line no-console
    console.log('[IdamStateService] createA...State$ start');

    return this.router.events.pipe(
      filter((type) => type instanceof RoutesRecognized),
      // eslint-disable-next-line no-magic-numbers
      take(1),
      tap(() => {
        // eslint-disable-next-line no-console
        console.log('[IdamStateService] createA...State$ RoutesRecognized passed');
      }),
      switchMap(() => authState$())
    );
  }

  private configureOAuth$(): Observable<AuthorizationState> {
    if (!isPlatformBrowser(this.platformId)) {
      return of({ type: AuthorizationStateType.LoggedOut });
    }

    this.oauthService.configure(createAuthConfig());

    this.activeFeaturesConfigCatService
      .isFeatureEnabledReadyForCurrentCountry$(FF_ACCOUNT_INFO_V2)
      .pipe(
        tap((isEnabled) => {
          // eslint-disable-next-line no-console
          console.log(`[IdamStateService] configureOA...$ FF_ACCOUNT_INFO_V2 resolved to ${isEnabled}`);
        }),
        take(NUMBER_1),
        mergeMap((isEnabled) => {
          return this.isSilentLoginDisabled$.pipe(map((isSilentLoginDisabled) => [isEnabled, isSilentLoginDisabled]));
        }),
        tap((isEnabled) => {
          // eslint-disable-next-line no-console
          console.log(`[IdamStateService] configureOA...$ isSilentLoginDisabled$ resolved to ${isEnabled[1]}`);
        }),
      )
      .subscribe(([isEnabled, isSilentLoginDisabled]) => {
        if (!isEnabled && !isSilentLoginDisabled) {
          // eslint-disable-next-line no-console
          console.log(`[IdamStateService] configureOA...$ setup silent refresh`);
          this.oauthService.setupAutomaticSilentRefresh(
            {
              realm_id: this.IDAM_SSO_REALM,
              country_code: getCountryDomainCode(this.webAppBuyerRequest).toUpperCase(),
              user_type: 'CUST',
            },
            'access_token',
            false
          );
        }
      });

    this.oauthService.timeoutFactor = TIME_OUT_FACTOR_FOR_ACCESS_TOKEN_REFRESH;

    return from(this.oauthService.loadDiscoveryDocument()).pipe(
      concatMap(() => {
        if (this.oauthService.hasValidAccessToken() || isSoftLoggedIn()) {
          return this.authorize$(LoginType.AutoLogin);
        } else {
          return from(this.oauthService.tryLogin()).pipe(
            filter(() => !this.oauthService.hasValidAccessToken()),
            concatMap(() => {
              if (this.url.includes('infra.metro-markets.cloud')) {
                return of({ type: AuthorizationStateType.LoggedOut });
              }
              this.oAuthSilentRefresh();
              return of({ type: AuthorizationStateType.Pending });
            }),
            catchError((error) => {
              if (isPlatformBrowser(this.platformId)) {
                this.serverLogger.error('IDAM Login failed', { error });
              }

              return of({ type: AuthorizationStateType.LoggedOut });
            })
          );
        }
      })
    );
  }

  private OAuthErrorEvents() {
    this.oauthService.events.subscribe((e) => {
      if (e instanceof OAuthErrorEvent) {
        UrlUtil.removeQueryParameters(['error_description', 'error'], this.router);
      }
    });
  }

  private listenOAuthLoginEvents$(): Observable<AuthorizationState> {
    return combineLatest([this.oauthService.events, this.identityService.isAuthorized$]).pipe(
      filter(([event, isAuthorized]) => !isAuthorized && ['token_received'].includes(event.type)),
      tap(() =>
        this.activeFeaturesConfigCatService
          .isFeatureEnabledForCurrentCountry$(FF_SL_SYNC_SSO_FOR_REACT_APP)
          .pipe(
            switchMap(async (isSyncSSOEnabled) =>
              isSyncSSOEnabled
                ? syncSSOForReactApp({
                    accessToken: this.oauthService.getAccessToken(),
                    expiresAt: this.oauthService.getAccessTokenExpiration(),
                  })
                : null
            )
          )
          .subscribe()
      ),
      switchMap(() => this.authorize$()),
      switchMap((event) => {
        this.exponeaService.sendNewCustomerData('login');
        return of(event);
      })
    );
  }

  private listenOAuthLogoutEvents$(): Observable<AuthorizationState> {
    return this.oauthService.events.pipe(
      filter((e) => e instanceof OAuthErrorEvent || ['logout', 'silent_refresh_error'].includes(e.type)),
      map((e) => {
        this.identityService.unauthorize();
        return {
          type: AuthorizationStateType.LoggedOut,
        };
      })
    );
  }

  private authorize$(eventType = LoginType.NormalLogin) {
    return this.identityService.authorize(this.createToken()).pipe(
      mapTo({
        type: AuthorizationStateType.LoggedIn,
      }),
      tap(() => {
        if (eventType === LoginType.NormalLogin) {
          this.isNormalLoginSubject.next(true);
          this.applicationEventService.publish(
            new AuthorizationApplicationEvent(
              ApplicationAuthorizationActionType.DestinationLogin,
              LoginEventsLabel.SuccessNormalLogin
            ),
            true
          );
        } else {
          this.applicationEventService.publish(
            new AuthorizationApplicationEvent(
              ApplicationAuthorizationActionType.DestinationLogin,
              LoginEventsLabel.SuccessAutoLogin
            ),
            true
          );
        }
      }),
      catchError((e) => {
        // eslint-disable-next-line no-console
        console.log(`[IdamStateService] a...orize$ error ${e?.message}. Doing Logout`);

        this.identityService.unauthorize();

        // this is a dirty code copy-paste from AuthService.logout to avoid circular DI error
        // it can be removed in 1 day after idam fixes they issue and map all metrox users
        const logoutIframe = document.createElement('iframe');
        logoutIframe.id = 'idam-iframe-op_seassion_end';
        logoutIframe.src = `${this.oauthService.logoutUrl}?id_token_hint=${this.oauthService.getIdToken()}`;
        document.body.append(logoutIframe);
        this.oauthService.logOut(true);
        // end of copy paste

        if (eventType === LoginType.NormalLogin) {
          this.applicationEventService.publish(
            new AuthorizationApplicationEvent(
              ApplicationAuthorizationActionType.DestinationLogin,
              LoginEventsLabel.FailNormalLogin
            ),
            true
          );
        } else {
          this.applicationEventService.publish(
            new AuthorizationApplicationEvent(
              ApplicationAuthorizationActionType.DestinationLogin,
              LoginEventsLabel.FailAutoLogin
            ),
            true
          );
        }

        return of({
          type: AuthorizationStateType.LoggedOut,
        });
      })
    );
  }

  private createToken(): Token {
    return {
      token_type: 'Bearer',
      access_token: this.oauthService.getAccessToken(),
      refresh_token: this.oauthService.getRefreshToken(),
    };
  }
}
